import { ButtonRoot } from '@/components/common/Button'
import { InputMenu } from '@/components/common/Input'
import { Icon } from '@/components/icons/Icon'
import { type CardRefType } from '@/defs/card'
import { CLIPBOARD_CARD_STATUS_LABELS } from '@/defs/clipboard'
import { type RecType } from '@/defs/rec'
import { useClipboardButton } from '@/hooks/useClipboardButton'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'
import { cn } from '@/utils/commonUtil'
import { getRecCardRefs } from '@/utils/recUtil'

import styles from '../CardControls.module.scss'

import { CardControlsClipboardOption } from './CardControlsClipboardOption/CardControlsClipboardOption'

type Props = {
  rec: RecType
}

type CardControlsClipboardBranchProps = {
  cardRefs: CardRefType[]
}

const CardControlsClipboardSingle = ({ cardRefs }: CardControlsClipboardBranchProps) => {
  const { isAllInClipboard, onToggleAllClick } = useClipboardButton({ cardRefs })

  const status = isAllInClipboard ? 'saved' : 'unsaved'
  const { actionLabel, icon } = CLIPBOARD_CARD_STATUS_LABELS[status]
  const colorScheme = isAllInClipboard ? 'success' : undefined

  return (
    <ButtonRoot
      aria-label={actionLabel}
      className={cn(styles.button, { colorScheme, styles })}
      onClick={onToggleAllClick}
      title={actionLabel}
    >
      <Icon name={icon} />
    </ButtonRoot>
  )
}

const CardControlsClipboardMultiple = ({ cardRefs }: CardControlsClipboardBranchProps) => {
  const { cardsInClipboard, isAllInClipboard, isSomeInClipboard } = useClipboardButton({ cardRefs })

  const status = isAllInClipboard ? 'saved' : 'unsaved'
  const { icon } = CLIPBOARD_CARD_STATUS_LABELS[status]
  const colorScheme = isAllInClipboard ? 'success' : undefined

  const menuOptions = cardRefs.map((cardRef) => ({
    label: <CardControlsClipboardOption cardRef={cardRef} />,
  }))

  return (
    <InputMenu
      aria-label='Clipboard Options'
      className={cn(styles.button, { colorScheme, iconsDouble: isSomeInClipboard, styles })}
      options={menuOptions}
      title='Clipboard Options'
    >
      {isSomeInClipboard ? (
        <>
          {cardsInClipboard.slice(0, 2).map((c, i) => (
            <span
              className={cn(styles.iconDouble, { colorScheme: c.status === 'saved' ? 'success' : undefined, styles })}
              key={i}
            >
              <Icon name={CLIPBOARD_CARD_STATUS_LABELS[c.status].icon} />
            </span>
          ))}
        </>
      ) : (
        <Icon name={icon} />
      )}
    </InputMenu>
  )
}

export const CardControlsClipboard = ({ rec }: Props) => {
  const cardRefs = getRecCardRefs(rec)

  return (
    <li className={styles.item} {...getClickawayIgnoreProps(['collapse-expanded'])}>
      {cardRefs.length === 1 ? (
        <CardControlsClipboardSingle cardRefs={cardRefs} />
      ) : (
        <CardControlsClipboardMultiple cardRefs={cardRefs} />
      )}
    </li>
  )
}
